import APIService from 'services/APIService'

const api = APIService()

// Tasks
export const getTaskStats = (params = {}) => api.get('/tasks/stats', { params })
export const getTaskBuildings = () => api.get('/tasks/buildings')
export const getTaskAssignees = () => api.get('/tasks/assignees')
export const getTaskApprovers = () => api.get('/tasks/approvers')
export const getRecurringTasks = () => api.get('/tasks/recurring')
export const getTaskAnalytics = (params = {}) => api.get('/tasks/analytics', { params })
export const getActionStatsScoped = (params = {}) => api.get('/actions/stats', { params })
export const getTasks = (params = {}) => api.get('/tasks', { params })
export const getTask = id => api.get(`/tasks/${id}`)

export const getTaskOccurrences = id => api.get(`/tasks/${id}/occurrences`)
export const createTask = payload => api.post('/tasks', payload)
export const updateTask = ({ id, ...data }) => api.patch(`/tasks/${id}`, data)
export const deleteTask = id => api.delete(`/tasks/${id}`)
export const replicateTask = id => api.post(`/tasks/${id}/replicate`)
export const assignTask = ({ taskId, assigneeIds, scope }) =>
  api.post(`/tasks/${taskId}/assign`, { assigneeIds, scope })
export const sendTaskReminder = ({ taskId, recipientIds, note }) =>
  api.post(`/tasks/${taskId}/remind`, { recipientIds, note })
export const previewAssignmentWarnings = ({ taskId, assigneeIds }) =>
  api.post(`/tasks/${taskId}/assign/preview`, { assigneeIds })
// CSV download — issues a GET with the current filter set so the
// export matches what's on screen. `responseType: 'blob'` keeps
// axios-case-converter from trying to parse the stream as JSON.
export const downloadTasksCsv = (params = {}) =>
  api.get('/tasks/export.csv', { params, responseType: 'blob' })
export const updateApprovers = ({ taskId, approverIds, scope }) =>
  api.put(`/tasks/${taskId}/approvers`, { approverIds, scope })
export const submitTask = id => api.post(`/tasks/${id}/submit`)
export const approveTask = id => api.post(`/tasks/${id}/approve`)
export const rejectTask = ({ id, reason }) =>
  api.post(`/tasks/${id}/reject`, { reason })
export const toggleStarTask = id => api.post(`/tasks/${id}/star`)
export const stopRecurrence = id => api.post(`/tasks/${id}/stop-recurrence`)
export const holdTask = ({ id, reason, nextAction }) =>
  api.post(`/tasks/${id}/hold`, { reason, nextAction })
export const resumeTask = id => api.post(`/tasks/${id}/resume`)
export const signOffTask = ({ id, notes, signature }) =>
  api.post(`/tasks/${id}/sign-off`, { notes, signature })
export const bulkSignOffTasks = ({ taskIds, notes }) =>
  api.post('/tasks/bulk-sign-off', { taskIds, notes })
export const selectAllTaskIds = (params = {}) =>
  api.get('/tasks/select-all-ids', { params })
export const syncBlockers = ({ taskId, blockerIds }) =>
  api.post(`/tasks/${taskId}/blockers`, { blocker_ids: blockerIds })
export const attachReferenceFiles = ({ taskId, fileIds, label }) =>
  api.post(`/tasks/${taskId}/reference-files`, { fileIds, label })
export const detachReferenceFile = ({ taskId, fileId }) =>
  api.delete(`/tasks/${taskId}/reference-files/${fileId}`)
export const getTaskQrCode = ({ taskId, format = 'svg', size = 320 }) =>
  api.get(`/tasks/${taskId}/qr-code`, {
    params: { format, size },
    responseType: 'blob',
  })
export const getRecurringTaskQrCode = ({ taskId, format = 'svg', size = 320 }) =>
  api.get(`/tasks/${taskId}/recurring-qr-code`, {
    params: { format, size },
    responseType: 'blob',
  })
export const getObligationTaskQrCode = ({ taskId, format = 'svg', size = 320 }) =>
  api.get(`/tasks/${taskId}/obligation-qr-code`, {
    params: { format, size },
    responseType: 'blob',
  })
export const resolveTaskSeries = seriesRootId =>
  api.get(`/tasks/series/${seriesRootId}/current`)
export const resolveObligationTask = obligationId =>
  api.get(`/tasks/obligation/${obligationId}/current`)
export const searchAssignableUsers = ({ search, buildingPassportId }) =>
  api.get('/tasks/assignable-users', {
    params: { search, building_passport_id: buildingPassportId }
  })
export const assignChecklistItem = ({
  bpId,
  templateId,
  itemId,
  assigneeUserId,
  assigneeUserIds,
  rules,
  createTasks
}) =>
  api.post(
    `/building-passport/${bpId}/analytics-template/${templateId}/items/${itemId}/assignment`,
    {
      assignee_user_id: assigneeUserId || null,
      assignee_user_ids: Array.isArray(assigneeUserIds) ? assigneeUserIds : undefined,
      rules: Array.isArray(rules) ? rules : undefined,
      create_tasks: Boolean(createTasks)
    }
  )
export const getChecklistItemTasks = ({ bpId, templateId, itemId }) =>
  api.get(
    `/building-passport/${bpId}/analytics-template/${templateId}/items/${itemId}/tasks`
  )
export const getChecklistItemOverview = ({ bpId, templateId, itemId }) =>
  api.get(
    `/building-passport/${bpId}/analytics-template/${templateId}/items/${itemId}/overview`
  )
export const saveFormResponse = ({ taskId, responses, currentVersion, submissionLocation }) =>
  api.post(`/tasks/${taskId}/form-response`, {
    responses,
    currentVersion,
    submissionLocation: submissionLocation || null,
  })
export const saveApproverFormResponse = ({
  taskId,
  responses,
  currentVersion,
  submissionLocation,
}) =>
  api.post(`/tasks/${taskId}/approver-form-response`, {
    responses,
    currentVersion,
    submissionLocation: submissionLocation || null,
  })
export const rePopulateFormFields = taskId =>
  api.post(`/tasks/${taskId}/form/re-populate`)
export const getFormVersions = taskId =>
  api.get(`/tasks/${taskId}/form-versions`)
export const getConflicts = taskId => api.get(`/tasks/${taskId}/conflicts`)
// External assignees
export const assignExternalUser = ({ taskId, email, name, expiresAt, scope }) =>
  api.post(`/tasks/${taskId}/assign-external`, {
    email,
    name,
    expires_at: expiresAt,
    scope
  })
export const removeExternalAssignee = ({ taskId, externalAssigneeId, scope }) =>
  api.delete(`/tasks/${taskId}/external-assignees/${externalAssigneeId}${scope === 'future' ? '?scope=future' : ''}`)

export const getExternalTaskQrCode = ({ taskId, externalAssigneeId, format = 'svg', size = 320 }) =>
  api.get(`/tasks/${taskId}/external-assignees/${externalAssigneeId}/qr-code`, {
    params: { format, size },
    responseType: 'blob',
  })

// External task access (no auth required)
export const getExternalTask = shareToken =>
  api.get(`/external-tasks/${shareToken}`)
export const saveExternalFormResponse = ({
  shareToken,
  responses,
  currentVersion
}) =>
  api.post(`/external-tasks/${shareToken}/form-response`, {
    responses,
    currentVersion
  })
export const submitExternalTask = shareToken =>
  api.post(`/external-tasks/${shareToken}/submit`)

// Form Files
export const getFormFiles = taskId => api.get(`/tasks/${taskId}/form-files`)
export const generateFormFileUploadUrl = ({
  taskId,
  fieldKey,
  filename,
  contentType
}) =>
  api.post(`/tasks/${taskId}/form-files/upload-url`, {
    field_key: fieldKey,
    filename,
    content_type: contentType
  })
export const confirmFormFileUpload = ({ taskId, fileId, fieldKey }) =>
  api.post(`/tasks/${taskId}/form-files/${fileId}/confirm`, {
    field_key: fieldKey
  })
export const deleteFormFile = ({ taskId, fileId }) =>
  api.delete(`/tasks/${taskId}/form-files/${fileId}`)

// Bulk Operations
export const bulkDeleteTasks = taskIds =>
  api.post('/tasks/bulk-delete', { taskIds })
export const bulkUpdateTasks = ({ taskIds, ...fields }) =>
  api.patch('/tasks/bulk-update', { taskIds, ...fields })
export const bulkAssignTasks = ({ taskIds, userIds, role, mode, setObligationDefault }) =>
  api.post('/tasks/bulk-assign', { taskIds, userIds, role, mode, setObligationDefault })
export const bulkAssignExternalUsers = ({ taskIds, people, mode }) =>
  api.post('/tasks/bulk-assign-external', { taskIds, people, mode })
export const getBulkAssignmentSummary = taskIds =>
  api.get('/tasks/bulk-assignment-summary', {
    params: { task_ids: taskIds },
    paramsSerializer: { indexes: false }
  })

// Teams
export const getTeams = (params = {}) => api.get('/task-teams', { params })
export const getTeam = id => api.get(`/task-teams/${id}`)
export const createTeam = payload => api.post('/task-teams', payload)
export const updateTeam = ({ id, ...data }) =>
  api.patch(`/task-teams/${id}`, data)
export const deleteTeam = id => api.delete(`/task-teams/${id}`)
export const addTeamMember = ({ teamId, userId, email, name }) =>
  api.post(`/task-teams/${teamId}/members`, {
    ...(userId ? { userId } : { email, ...(name ? { name } : {}) })
  })
export const removeTeamMember = ({ teamId, memberId }) =>
  api.delete(`/task-teams/${teamId}/members/${memberId}`)

// Projects
export const getProjects = (params = {}) =>
  api.get('/task-projects', { params })
export const createProject = payload => api.post('/task-projects', payload)
export const updateProject = ({ id, ...data }) =>
  api.patch(`/task-projects/${id}`, data)
export const deleteProject = id => api.delete(`/task-projects/${id}`)

// Form Templates
export const getFormTemplates = (params = {}) => {
  const {
    search,
    category,
    scope,
    visibility,
    buildingPassportId,
    includeHidden,
    ...rest
  } = params
  const query = []
  if (search) query.push(`filter[search]=${encodeURIComponent(search)}`)
  if (category) query.push(`filter[category]=${encodeURIComponent(category)}`)
  if (scope) query.push(`filter[scope]=${encodeURIComponent(scope)}`)
  if (visibility) query.push(`filter[visibility]=${encodeURIComponent(visibility)}`)
  if (buildingPassportId) {
    query.push(`filter[building_passport_id]=${encodeURIComponent(buildingPassportId)}`)
  }
  if (includeHidden) query.push('include_hidden=1')
  const qs = query.join('&')
  const url = qs ? `/form-templates?${qs}` : '/form-templates'
  return api.get(url, { params: rest })
}
export const getFormTemplate = id => api.get(`/form-templates/${id}`)
export const createFormTemplate = payload =>
  api.post('/form-templates', payload)
export const updateFormTemplate = ({ id, ...data }) =>
  api.patch(`/form-templates/${id}`, data)
export const deleteFormTemplate = id => api.delete(`/form-templates/${id}`)
export const publishFormTemplate = id =>
  api.post(`/form-templates/${id}/publish`)
export const cloneFormTemplate = id => api.post(`/form-templates/${id}/clone`)
export const updateFormTemplateSharing = ({ id, ...data }) =>
  api.put(`/form-templates/${id}/sharing`, data)

// Template Assignments (renewal)
export const getTemplateAssignments = templateId =>
  api.get(`/form-templates/${templateId}/assignments`)
export const createTemplateAssignment = ({ templateId, ...data }) =>
  api.post(`/form-templates/${templateId}/assignments`, data)
export const deleteTemplateAssignment = ({ templateId, assignmentId }) =>
  api.delete(`/form-templates/${templateId}/assignments/${assignmentId}`)

// Project Dashboard
export const getProjectDashboard = (params = {}) =>
  api.get('/tasks/project-dashboard', { params })

// Maintenance Types (for expiry wizard)
export const getMaintenanceTypes = () => api.get('/tasks/maintenance-types')

// Task Settings
export const getTaskSettings = buildingPassportId =>
  api.get('/task-settings', {
    params: { building_passport_id: buildingPassportId }
  })
export const updateTaskSettings = ({ buildingPassportId, ...data }) =>
  api.put('/task-settings', { buildingPassportId, ...data })

// Task Reports
export const getTaskReports = ({
  buildingPassportId,
  page = 1,
  perPage = 15
}) =>
  api.get(`/building-passport/${buildingPassportId}/task-reports`, {
    params: { page, per_page: perPage }
  })
export const downloadTaskReport = ({ buildingPassportId, reportId }) =>
  api.get(
    `/building-passport/${buildingPassportId}/task-reports/${reportId}/download`
  )
export const regenerateTaskReport = ({ buildingPassportId, taskId }) =>
  api.post(
    `/building-passport/${buildingPassportId}/task-reports/${taskId}/regenerate`
  )
export const getTaskReportVersions = ({ buildingPassportId, taskId }) =>
  api.get(
    `/building-passport/${buildingPassportId}/task-reports/${taskId}/versions`
  )

export const generateFormFromDocument = ({ file, buildingPassportId, organisationId, targetType, targetId }) => {
  const form = new FormData()
  form.append('file', file)
  if (buildingPassportId) form.append('building_passport_id', buildingPassportId)
  if (organisationId) form.append('organisation_id', organisationId)
  if (targetType && targetId) {
    form.append('target_type', targetType)
    form.append('target_id', targetId)
  }
  return api.post('/me/form-templates/generate', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const getFormGeneration = id =>
  api.get(`/me/form-templates/generations/${id}`)

export const getManagedBuildingPassports = () =>
  api.get('/me/managed-building-passports')

export const getFormSharingOptions = () =>
  api.get('/me/form-templates/sharing-options')
